import React from 'react'
import injectJss from 'react-jss'
import compose from 'recompose/compose'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { PanoPlayer, VIDEO_STATE } from 'aria-viewer/common/components/PanoPlayer'
import PreviewTitleDescription from '../preview/PreviewTitleDescription'
import CHECK_CIRCLE from 'public/assets/svg/check_circle.svg'
import FRESNEL from 'public/assets/svg/fresnel_small.png'

const HOVER_TRANSITION_TIME = 0.28
const HOVER_EASE = 'ease-in-out'
const SPHERE_SIZE = 68

const style = theme => ({
    triggerContainer: {
        pointerEvents: 'all',
    },
    icon: {
        color: '#fff',
        width: SPHERE_SIZE,
        height: SPHERE_SIZE,
        border: 'none',
        filter: 'none',
        zIndex: 501,
        top: '0px',
        transform: 'translateX(45px)',
        position: 'relative',
        userSelect: 'none',
        transition: `all ${HOVER_TRANSITION_TIME}s ${HOVER_EASE}`,
    },
    preview: {
        width: SPHERE_SIZE,
        height: SPHERE_SIZE,
        mixBlendMode: 'overlay',
        backgroundImage: `linear-gradient(132deg, rgba(255, 255, 255, 0), ${theme.palette.common.faintBlack})`,
        boxShadow: '0 10px 15px 0 rgba(0, 0, 0, 0.5)',
        borderRadius: '50%',
        zIndex: 500,
        position: 'absolute',
        transition: `all ${HOVER_TRANSITION_TIME}s ${HOVER_EASE}`,
        transform: 'translateY(-50%) translateX(-50%)',
        userSelect: 'none',
        '& *': {
            pointerEvents: 'none',
        },
        cursor: ({ currentState }) => (currentState === 'panning' ? 'default' : 'pointer'),
        '& canvas, .placeholder-image, #canvas-placeholder': {
            width: SPHERE_SIZE,
            height: SPHERE_SIZE,
            textAlign: 'center',
            borderRadius: '50%',
            position: 'absolute',
        },
        '& .placeholder-image': {
            transition: `all ${HOVER_TRANSITION_TIME}s ${HOVER_EASE}`,
            opacity: 1,
            zIndex: 503,
        },
        '& canvas, #canvas-placeholder': {
            transition: `all ${HOVER_TRANSITION_TIME}s ${HOVER_EASE}`,
            opacity: 0,
            zIndex: 502,
        },
        '& #canvas-placeholder': {
            backgroundColor: 'black',
            opacity: 0,
            zIndex: 502,
        },
    },
    panoUmounted: {
        '& #canvas-placeholder': {
            animation: 'fade-blend .25s ease-in-out forwards',
        },
        '& .placeholder-image': {
            animation: 'image-reveal .25s ease-in-out forwards',
        },
    },
    '@keyframes fade-blend': {
        '0%': {
            opacity: 0.1,
            zIndex: 506,
        },
        '5%': {
            opacity: 1,
            zIndex: 506,
        },
        '60%': {
            opacity: 0,
            zIndex: 502,
        },
    },
    '@keyframes image-reveal': {
        '0%': {
            opacity: 0,
        },
        '20%': {
            opacity: 1,
        },
        '100%': {
            opacity: 1,
        },
    },
    previewHover: {
        boxShadow: '0 10px 15px 0 rgba(0, 0, 0, 0.5)',
        transform: 'translateY(-50%) translateX(-50%) scale(1.45)',
        transition: `all ${HOVER_TRANSITION_TIME}s ${HOVER_EASE}`,
        // boxShadow: '20px 20px 20px 35px  rgba(0, 0, 0, 0.2)',
        '& .placeholder-image,  #canvas-placeholder': {
            transition: `all ${HOVER_TRANSITION_TIME}s ${HOVER_EASE}`,
            textAlign: 'center',
            borderRadius: '50%',
            position: 'absolute',
            transform: 'scale(2)',
        },
        '& canvas': {
            transition: `all ${HOVER_TRANSITION_TIME}s ${HOVER_EASE}`,
            textAlign: 'center',
            borderRadius: '50%',
            position: 'absolute',
            transform: 'scale(2)',
        },
        // '& .icon': {
        //   transition: `scale ${HOVER_TRANSITION_TIME}s ${HOVER_EASE}`,
        // //   transform: 'scale(0.2)',
        //   opacity: '1'
        // },
    },
    previewPanning: {
        transform: 'translateY(-50%) translateX(-50%) scale(1.5)',
        transition: `all ${HOVER_TRANSITION_TIME}s ${HOVER_EASE}`,
        // boxShadow: '2px 2px 2px 4px  rgba(0, 0, 0, 0.2)',
    },
    panoLoaded: {
        '& .placeholder-image,  #canvas-placeholder': {
            animation: 'image-hide 0.3s ease-in-out forwards',
        },
        '& canvas': {
            animation: 'canvas-reveal 0.3s ease-in-out forwards',
        },
    },
    '@keyframes canvas-reveal': {
        '0%': {
            opacity: 0,
        },
        '100%': {
            opacity: 1,
        },
    },
    '@keyframes image-hide': {
        '0%': {
            opacity: 1,
        },
        '100%': {
            opacity: 0,
        },
    },
    previewImage: {
        backgroundImage: ({ thumbnailUrl }) => `url(${thumbnailUrl})`,
    },
    fresnel: {
        // https://css-tricks.com/tinted-images-multiple-backgrounds/
        zIndex: 509,
        position: 'absolute',
        width: SPHERE_SIZE,
        height: SPHERE_SIZE,
        opacity: 1,
        background: [
            `url(${FRESNEL})`,
            'radial-gradient(ellipse at 50% 30%, rgba(255,255,255,0) 0%,rgba(255,255,255,0) 39%,rgba(255,255,255,0.31) 57%,rgba(20,20,20,0.56) 72%,rgba(20,20,20,1) 98%)',
        ],
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100%',
        transition: `all ${HOVER_TRANSITION_TIME}s ${HOVER_EASE}`,
        borderRadius: '50%',
        filter: 'contrast(4) brightness(0.7)',
    },
    border: {
        border: '0.5px solid rgb(192,192,192)',
        transition: `all ${HOVER_TRANSITION_TIME}s ${HOVER_EASE}`,
        zIndex: 509,
        position: 'absolute',
        width: SPHERE_SIZE,
        height: SPHERE_SIZE,
        borderRadius: '50%',
        filter: 'blur(15px)',
    },
    borderHover: {
        transform: 'scale(2)',
    },
    fresnelHover: {
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100%',
        transform: 'scale(2)',
        transition: `all ${HOVER_TRANSITION_TIME}s ${HOVER_EASE}`,
        pointerEvents: 'all',
    },
    iconHover: {
        transform: 'translateX(45px)',
    },
    triggerTextContainer: {
        pointerEvents: 'none',
        '& > div': {
            zIndex: 510,
            bottom: '-40px',
            '& > h1': {
                fontSize: '24px',
            },
        },
    },
})

class Trigger extends React.PureComponent {
    constructor(props) {
        super(props)
        this.state = {
            hoverState: false,
            panoLoaded: false,
            panoUnMounted: false,
            videoState: VIDEO_STATE.PLAYING,
            hoverText: '',
            hoverDescription: '',
        }
    }

    setHoverTrue = () => {
        const { onMouseEnter, hasHover, text, description } = this.props
        // Gate this behind hasHover boolean that hover doesn't occur in certain size ranges on embed
        if (hasHover) {
            onMouseEnter && onMouseEnter()

            if (!this.state.hoverState) {
                this.setState({
                    hoverState: true,
                    panoUnMounted: false,
                    videoState: VIDEO_STATE.PLAYING,
                    hoverText: text,
                    hoverDescription: description,
                })
            }
        }
    }

    setPanoLoaded = () =>
        this.setState({
            panoLoaded: true,
        })

    setHoverFalse = () => {
        const { onMouseLeave } = this.props

        onMouseLeave && onMouseLeave()

        this.setState({
            hoverState: false,
            panoLoaded: false,
            videoState: VIDEO_STATE.PAUSED,
            hoverText: '',
            hoverDescription: '',
        })
    }

    setPanoUnMounted = () =>
        this.setState({
            panoUnMounted: true,
        })

    onClick = e => {
        const { onClick } = this.props

        onClick && onClick(e, this)
    }

    videoFrameAdvance = event => {
        const { currentTime } = event.target
        const { onPreviewComplete, previewLength } = this.props

        // we wantto stop the video after 10 seconds
        if (currentTime >= previewLength) {
            this.setState({ videoState: VIDEO_STATE.PAUSED })
            if (typeof onPreviewComplete === 'function') {
                onPreviewComplete()
            }
        }
    }

    render() {
        const { hoverState, panoLoaded, panoUnMounted, videoState, hoverText, hoverDescription } = this.state

        const { classes, thumbnailUrl, title, videoUrl, currentState } = this.props
        return (
            <div className={classNames('trigger', classes.triggerContainer)}>
                <div
                    className={classNames(
                        'preview',
                        classes.preview,
                        { [classes.previewPanning]: currentState === 'panning' && !hoverState },
                        { [classes.previewHover]: hoverState },
                        { [classes.panoLoaded]: panoLoaded },
                        { [classes.panoUmounted]: panoUnMounted }
                    )}
                    onMouseEnter={this.setHoverTrue}
                    onMouseLeave={this.setHoverFalse}
                    onClick={this.onClick}
                    onTouchStart={this.onClick}
                >
                    <div className={classNames(classes.fresnel, { [classes.fresnelHover]: hoverState })}>
                        {currentState === 'visited' && (
                            <div className={classNames(classes.icon, { [classes.iconHover]: hoverState }, 'icon')}>
                                <img aria-hidden="true" alt="Checkmark" src={CHECK_CIRCLE} />
                            </div>
                        )}
                    </div>
                    <div className={classNames(classes.border, { [classes.borderHover]: hoverState })} />
                    <img src={thumbnailUrl} alt={'default'} className="placeholder-image" />
                    {hoverState && (
                        <PanoPlayer
                            sourceURL={videoUrl}
                            width={this.props.expandedWidth}
                            height={this.props.expandedHeight}
                            muted={this.props.muted}
                            videoState={videoState}
                            onTextureLoaded={this.setPanoLoaded}
                            onUnMount={this.setPanoUnMounted}
                            onTimeUpdate={this.videoFrameAdvance}
                        />
                    )}

                    <div className={classes.triggerTextContainer}>
                        <PreviewTitleDescription
                            title={hoverText}
                            description={hoverDescription}
                            isVisible={hoverState}
                            leftOffset={70}
                        />
                    </div>
                </div>
            </div>
        )
    }
}
Trigger.defaultProps = {
    expandedWidth: 480,
    expandedHeight: 480,
    muted: false,
    currentState: 'default',
    previewLength: 10,
    thumbnailUrl: '',
    videoUrl: '',
    onClick: () => {},
    location: {
        x: 0,
        y: 0,
        z: 0,
    },
}

Trigger.propTypes = {
    expandedWidth: PropTypes.number,
    expandedHeight: PropTypes.number,
    currentState: PropTypes.string,
    previewLength: PropTypes.number,
    thumbnailUrl: PropTypes.string,
    videoUrl: PropTypes.string,
    onClick: PropTypes.func,
    location: PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number,
        z: PropTypes.number,
    }),
}

export default compose(injectJss(style))(Trigger)
